import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import ImpersonationBanner from "./shell/ImpersonationBanner";

const SHARE_TOKEN_KEY = "cortex_share_access_token";

export default function ProtectedRoute({ children }: { children?: React.ReactNode }) {
  const { isAuthenticated, isLoading, isLoggingIn, user } = useAuth();
  const location = useLocation();

  // Share-mode: visitor came via /share/<token>, was issued a
  // short-lived JWT we stored in sessionStorage, and was redirected
  // to the regular docs URL. The server treats them like a logged-in
  // user with scope-limited access; the auth gate here just needs to
  // recognize that the share-session JWT is present so the visitor
  // doesn't get bounced to /login. Legacy ?share=<token> query is
  // also accepted for older share URLs already in circulation.
  const hasShareSession = (() => { try { return !!sessionStorage.getItem(SHARE_TOKEN_KEY); } catch { return false; } })();
  const hasShareToken = hasShareSession || !!new URLSearchParams(location.search).get("share");

  // Wait for both auth and preview config to load before deciding
  if (isLoading && !hasShareToken) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <div className="size-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          {isLoggingIn ? "Signing in..." : "Loading..."}
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !hasShareToken) {
    const returnTo = location.pathname + location.search + location.hash;
    // Carry the app surface (first path segment) so the login page brands itself
    // for a partner-facing app — e.g. /enable → "Joveo Enable" with the
    // surface-specific branding. Sign-in itself is Atlas Identity either way.
    // On the real host (enable.joveo.ai) detectSurface() also resolves from the
    // hostname; this makes it correct on localhost and any shared host too.
    const seg = location.pathname.split("/").filter(Boolean)[0];
    const surfaceParam = (seg === "enable" || seg === "docs") ? `&surface=${seg}` : "";
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}${surfaceParam}`} replace />;
  }

  // Force profile completion for invited users
  if (user && user.status === "invited" && location.pathname !== "/complete-profile") {
    return <Navigate to="/complete-profile" replace />;
  }

  // Every authenticated surface routes through here — LayoutShell, DocsShell,
  // EnableShell and SettingsLayout alike — so the band mounts once instead of
  // in each of them.
  return <ImpersonationBanner>{children ?? <Outlet />}</ImpersonationBanner>;
}
